/**
 * Feature Flag Row Component
 */

import { useState } from 'react';
import { Edit2, Trash2, ToggleLeft, ToggleRight, Users, Globe } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type { FeatureFlag } from '@/types/featureFlags';

interface FlagRowProps {
  name: string;
  config: FeatureFlag;
  onToggle: (flagName: string, enabled: boolean) => void;
  onEdit: (flagName: string) => void;
  onDelete: (flagName: string) => void;
  isToggling?: boolean;
}

export function FlagRow({
  name,
  config,
  onToggle,
  onEdit,
  onDelete,
  isToggling,
}: FlagRowProps) {
  const { t } = useI18n();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleToggle = () => {
    onToggle(name, !config.enabled);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete(name);
    setShowDeleteConfirm(false);
  };

  const getRegionLabel = (region: string) => {
    if (region === 'cn') return t('featureFlags.flagEditor.regionChina');
    if (region === 'global') return t('featureFlags.flagEditor.regionGlobal');
    return region;
  };

  return (
    <>
      <div className="flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggle}
              disabled={isToggling}
              className={`
                flex-shrink-0 transition-colors
                ${config.enabled ? 'text-green-600' : 'text-gray-400'}
                ${isToggling ? 'opacity-50' : 'hover:opacity-80'}
              `}
            >
              {config.enabled ? (
                <ToggleRight className="w-8 h-8" />
              ) : (
                <ToggleLeft className="w-8 h-8" />
              )}
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-medium text-gray-900">
                  {name}
                </span>
                {config.rollout_percentage < 100 && (
                  <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                    {config.rollout_percentage}% {t('featureFlags.flagList.rollout')}
                  </span>
                )}
                {config.user_groups && config.user_groups.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                    <Users className="w-3 h-3" />
                    {config.user_groups.length === 1
                      ? config.user_groups[0]
                      : `${config.user_groups.length} ${t('featureFlags.flagList.groups')}`}
                  </span>
                )}
                {config.regions && config.regions.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                    <Globe className="w-3 h-3" />
                    {config.regions.map(r => getRegionLabel(r)).join(', ')}
                  </span>
                )}
              </div>
              {config.description && (
                <p className="text-sm text-gray-500 mt-0.5 truncate max-w-md">
                  {config.description}
                </p>
              )}
              <div className="flex gap-1 mt-1">
                {config.platforms.map((platform) => (
                  <span
                    key={platform}
                    className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                  >
                    {platform}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onEdit(name)}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteClick}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('featureFlags.deleteConfirm.title')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('featureFlags.deleteConfirm.message', { name })}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete}>
                {t('featureFlags.deleteConfirm.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
